import clsx from 'clsx'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { trackClickEvent } from 'src/analytics/gtm'

import styles from './MonthlyPickWidget.module.scss'
import RightArrow from '../../assets/svgIcons/RightArrow'
import { useHighlightedIdeas } from '../../hooks/useHighlightedIdeas'
import type { IdeaPreview } from '../../model/IdeaPreview'

export type WidgetMode = 'light' | 'dark'

export interface MonthlyPickWidgetProps {
  mode?: WidgetMode
  className?: string
}

const MONTHS = [
  'JANUARY',
  'FEBRUARY',
  'MARCH',
  'APRIL',
  'MAY',
  'JUNE',
  'JULY',
  'AUGUST',
  'SEPTEMBER',
  'OCTOBER',
  'NOVEMBER',
  'DECEMBER',
] as const

export const getCurrentMonthName = (): string => MONTHS[new Date().getMonth()]

const MonthlyPickWidget = ({ mode = 'light', className }: MonthlyPickWidgetProps) => {
  const navigate = useNavigate()
  const pushClick = (payload: Parameters<typeof trackClickEvent>[0]): void => {
    trackClickEvent(payload)
  }
  const [isWidgetHovered, setIsWidgetHovered] = useState(false)
  const [isButtonPressed, setIsButtonPressed] = useState(false)

  const highlightedIdeasQuery = useHighlightedIdeas({ page: 1, pageSize: 12 })
  const ideas: IdeaPreview[] = useMemo(() => highlightedIdeasQuery.data ?? [], [highlightedIdeasQuery.data])

  const currentMonth = useMemo(() => getCurrentMonthName(), [])
  const pickIdeas = useMemo(() => ideas.slice(1, 4), [ideas])
  const ideaCount = ideas.length

  function handleNavigate(): void {
    pushClick({
      module: 'Hero Banner',
      module_title: 'Monthly Pick Widget',
      click_text: 'Striking ideas to Discover',
      click_url: '/ideas-bank',
      click_element: 'card',
    })
    navigate('/ideas-bank')
  }
  if (!highlightedIdeasQuery.isLoading && pickIdeas.length === 0) {
    return null
  }

  return (
    <article
      className={clsx(styles.widget, styles[`widget--${mode}`], className, 'w-[444px] h-[214px] ml-[-85px]')}
      onMouseEnter={() => setIsWidgetHovered(true)}
      onMouseLeave={() => {
        setIsWidgetHovered(false)
        setIsButtonPressed(false)
      }}
      aria-label={`${currentMonth} Ideas Pick – ${ideaCount} striking ideas`}
    >
      <div className="flex flex-col gap-4 justify-between">
        <header className="flex items-center justify-between">
          <div className={styles.widget__label}>
            <h3
              className={`subtitle-xs ${styles.widget__title} ${mode === 'dark' ? 'text-[#BDBDBD]' : 'text-[#505050]'}`}
            >
              {currentMonth} IDEAS PICK
            </h3>
          </div>
        </header>

        <div className={`${styles['widget__count-block']} ${mode === 'dark' ? 'text-[#fff]' : 'text-[#000]'} `}>
          <span className={`${styles.widget__count} text-[48px] font-normal`}>{ideaCount}</span>
          <span className={` ${styles.widget__descriptor} subtitle-l`}>
            striking ideas
            <br />
            to discover
          </span>
        </div>
      </div>

      <div className={styles.widget__body}>
        <div className={styles['widget__images-container']}>
          {pickIdeas.map((idea: IdeaPreview, index: number) => (
            <div
              key={idea.id}
              className={clsx(
                styles['widget__image-wrapper'],
                styles[`widget__image-wrapper--${index}`],
                isWidgetHovered && styles['widget__image-wrapper--hovered'],
              )}
            >
              {idea.coverImageUrl ? (
                <img src={idea.coverImageUrl} alt={idea.name} className={styles.widget__image} loading="lazy" />
              ) : (
                <div className={styles['widget__image-placeholder']} aria-hidden="true" />
              )}
            </div>
          ))}
        </div>
        <button
          type="button"
          className={clsx(
            styles.widget__button,
            isButtonPressed && styles['widget__button--pressed'],
            mode === 'dark'
              ? 'border-2 border-white bg-[#000] hover:bg-[#6E6E6E]'
              : 'border-2 border-black bg-[#fff] hover:bg-[#D4D4D4]',
          )}
          onClick={handleNavigate}
          onMouseDown={() => setIsButtonPressed(true)}
          onMouseUp={() => setIsButtonPressed(false)}
          onMouseLeave={() => setIsButtonPressed(false)}
          aria-label="Go to Ideas Bank"
        >
          <RightArrow mode={mode === 'light'} />
        </button>
      </div>

      <div className={styles.widget__overlay} aria-hidden="true" />
    </article>
  )
}

export default MonthlyPickWidget
